/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import Select, {
  components,
  type GroupBase,
  type OptionProps,
  type SingleValueProps,
  type StylesConfig,
} from 'react-select';
import { LightInput } from 'ui';
import colors from 'ui/styles/colors.module.scss';
import sizes from 'ui/styles/sizes.module.scss';

import cx from 'classnames';

import s from './styles.module.scss';

interface PhoneCountry {
  country_code: string;
  name: string;
  phone_code: string;
  value?: string | number;
}

const customStyles = (error?: string | null): StylesConfig<PhoneCountry, false> => ({
  container: (styles, { selectProps }) => ({
    ...styles,
    zIndex: selectProps.menuIsOpen ? 999 : 2,
    marginBottom: Number(sizes.base) / 4,
    marginTop: Number(sizes.base) / 4,
  }),
  control: (base, { selectProps, isFocused }) => ({
    ...base,
    backgroundColor: colors.white,
    borderColor: error ? colors.error : colors.border,
    borderRightColor: error ? colors.error : colors.border,
    borderRadius: '4px 0 0 4px',
    boxShadow: isFocused && !error ? `0 0 0 1px ${colors.background}` : 'none',
    minHeight: 50,
    outline: 'none',
    zIndex: selectProps.menuIsOpen ? 999 : 2,
    cursor: 'pointer',
    width: 90,

    '&:hover': {
      borderColor: error ? colors.error : colors.border,
      borderRightColor: error ? colors.error : colors.border,
    },
  }),
  dropdownIndicator: (base, { selectProps }) => ({
    ...base,
    color: selectProps.menuIsOpen ? colors.primary : colors.primary,
    padding: 0,
    marginRight: Number(sizes.base) / 4,
    width: 12,

    '&:hover': {
      color: colors.primary,
    },
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    cursor: 'pointer',
    zIndex: 100,
    backgroundColor: isSelected ? colors.primary : colors.white,
    color: isSelected ? colors.background : colors.primary,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.background,
    },
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  menuList: (styles) => ({
    ...styles,
    backgroundColor: colors.white,
    width: 336,
  }),
  input: (styles) => ({
    ...styles,
    color: colors.primary,
  }),
  placeholder: (styles) => ({
    ...styles,
    color: colors.primary,
    display: 'none',
  }),
  singleValue: (styles) => ({
    ...styles,
    color: colors.primary,
    lineHeight: 1.2,
  }),
  multiValue: (styles) => ({
    ...styles,
    display: 'none',
  }),
  clearIndicator: (styles) => ({
    ...styles,
    display: 'none',
  }),
});

const flagUrl = '/flags.png';

const Option = (props: OptionProps<PhoneCountry, false, GroupBase<PhoneCountry>>) => {
  const { data, getStyles } = props;

  return (
    <components.Option {...props} className={cx(getStyles('option', props))}>
      <div className={s.option} id={`option_${data.country_code}`}>
        <div
          className={cx(s.optionFlag, s[data.country_code.toLowerCase()])}
          style={{ backgroundImage: `url(${flagUrl})` }}
        />
        <div className={s.optionLabel}>{`${data.name} +${data.phone_code}`}</div>
      </div>
    </components.Option>
  );
};

const SingleValue = (props: SingleValueProps<PhoneCountry, false, GroupBase<PhoneCountry>>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className={s.option}>
        <div
          className={cx(s.optionFlag, s[data.country_code.toLowerCase()])}
          style={{ backgroundImage: `url(${flagUrl})` }}
        />
        <div className={s.optionLabel}>{`+${data.phone_code}`}</div>
      </div>
    </components.SingleValue>
  );
};

interface PhoneInputProps {
  error?: string | null;
  styles?: StylesConfig<PhoneCountry, false> | null;
  placeholder?: string | null;
  handleOnChange: (value: string) => void;
  options?: PhoneCountry[];
}

interface PhoneInputState {
  country: PhoneCountry | null;
  phoneInput: string;
}

class PhoneInput extends React.Component<PhoneInputProps, PhoneInputState> {
  static type = {
    name: 'dark',
    style: {
      errorText: {
        color: colors.error,
      },
    },
  };

  static defaultProps = {
    error: null,
    styles: null,
    placeholder: null,
  };

  constructor(props: PhoneInputProps) {
    super(props);
    this.state = { country: null, phoneInput: '' };
  }

  handleSelectCountry = (country: PhoneCountry | null) => {
    this.setState({ country, phoneInput: '' });
    this.props.handleOnChange('');
  };

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const re = /^[0-9\b]+$/;
    const { value } = event.target;
    if (value === '' || re.test(value)) {
      this.setState({ phoneInput: value });
      if (this.state.country) {
        this.props.handleOnChange(`+${this.state.country.phone_code}${value}`);
      }
    }
  };

  render() {
    const { error, styles, placeholder, ...props } = this.props;
    const { country, phoneInput } = this.state;

    return (
      <div className={s.phoneInput}>
        <Select<PhoneCountry, false>
          {...props}
          styles={customStyles(error)}
          isSearchable={false}
          components={{ Option, SingleValue }}
          onChange={(v) => this.handleSelectCountry(v)}
          value={country}
        />
        <LightInput
          disabled={!country}
          type="text"
          placeholder={placeholder ?? undefined}
          value={phoneInput}
          onChange={(v) => this.handleInputChange(v)}
          className={cx(s.phoneInputInput, error && s.phoneInputError)}
        />
      </div>
    );
  }
}

export default PhoneInput;
